import AddOnPoliciesForm from '../screens/AddOnPoliciesForm'
import ModifyPolicyForm from '../screens/ModifyPoliciesForm'
import PoliciesForm from '../screens/PoliciesForm'
import PoliciesTabs from '../screens/PoliciesTabs'
import { ROLE, RouteWithAccessType } from './types'

export const POLICIES_ROUTE = '/policies/(cancel-policies|amendment-policies)'
export const ADD_ON_POLICIES_ROUTE = '/policies/add-on-policies'

// The same roles the cancel and amendment routes list. Like theirs, these lists are inert:
// the router and canAccessTab read requiredFeatures only.
const POLICY_READERS = [
  ROLE.ADMIN,
  ROLE.PROVIDER,
  ROLE.PROVIDER_MARKETING,
  ROLE.PROVIDER_IT,
  ROLE.PROVIDER_MANAGEMENT,
  ROLE.CHANNEL_ADMIN,
  ROLE.CHANNEL_ACTIVATION
]
const POLICY_WRITERS = [
  ROLE.ADMIN,
  ROLE.PROVIDER,
  ROLE.PROVIDER_MARKETING,
  ROLE.PROVIDER_IT,
  ROLE.PROVIDER_MANAGEMENT,
  ROLE.CHANNEL_ACTIVATION
]

const cancelPolicyPagesRoleManagement: RouteWithAccessType[] = [
  {
    route: POLICIES_ROUTE,
    exact: true,
    rolesListWithReadAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ADMIN,
      ROLE.CHANNEL_ACTIVATION
    ],
    rolesListWithWriteAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ACTIVATION
    ],
    requiredFeatures: ['policy.cancel.list', 'policy.amendment.list'],
    component: <PoliciesTabs />,
    // PoliciesTabs gates each tab with canAccessTab, which returns FALSE when a route
    // declares no tabs at all — there is no tabConfig to check. With none declared, the
    // screen redirected every visitor to /403, which had no route, so the page rendered
    // blank for everyone including admins.
    //
    // One feature per tab, split from the two the route already requires. No new
    // permission is introduced: a role reaching this route holds both by definition.
    // The role lists below are INERT today: canAccessTab reads only requiredFeatures.
    // They are populated anyway because their absence is the same shape as the bug this
    // fixes — if a guard ever starts reading tab roles, a tab with none would deny
    // everyone. They mirror the route's own lists exactly, so they widen nothing.
    tabs: [
      {
        params: { page: 'cancel-policies' },
        rolesListWithReadAccess: [
          ROLE.ADMIN,
          ROLE.PROVIDER,
          ROLE.PROVIDER_MARKETING,
          ROLE.PROVIDER_IT,
          ROLE.PROVIDER_MANAGEMENT,
          ROLE.CHANNEL_ADMIN,
          ROLE.CHANNEL_ACTIVATION
        ],
        rolesListWithWriteAccess: [
          ROLE.ADMIN,
          ROLE.PROVIDER,
          ROLE.PROVIDER_MARKETING,
          ROLE.PROVIDER_IT,
          ROLE.PROVIDER_MANAGEMENT,
          ROLE.CHANNEL_ACTIVATION
        ],
        requiredFeatures: ['policy.cancel.list']
      },
      {
        params: { page: 'amendment-policies' },
        rolesListWithReadAccess: [
          ROLE.ADMIN,
          ROLE.PROVIDER,
          ROLE.PROVIDER_MARKETING,
          ROLE.PROVIDER_IT,
          ROLE.PROVIDER_MANAGEMENT,
          ROLE.CHANNEL_ADMIN,
          ROLE.CHANNEL_ACTIVATION
        ],
        rolesListWithWriteAccess: [
          ROLE.ADMIN,
          ROLE.PROVIDER,
          ROLE.PROVIDER_MARKETING,
          ROLE.PROVIDER_IT,
          ROLE.PROVIDER_MANAGEMENT,
          ROLE.CHANNEL_ACTIVATION
        ],
        requiredFeatures: ['policy.amendment.list']
      }
    ],
    sections: {
      ADD_POLICY_BUTTON: {
        rolesListWithReadAccess: [
          ROLE.ADMIN,
          ROLE.PROVIDER,
          ROLE.PROVIDER_MARKETING,
          ROLE.PROVIDER_IT,
          ROLE.PROVIDER_MANAGEMENT,
          ROLE.CHANNEL_ADMIN,
          ROLE.CHANNEL_ACTIVATION
        ],
        rolesListWithWriteAccess: [
          ROLE.ADMIN,
          ROLE.PROVIDER,
          ROLE.PROVIDER_MARKETING,
          ROLE.PROVIDER_IT,
          ROLE.PROVIDER_MANAGEMENT,
          ROLE.CHANNEL_ACTIVATION
        ],
        requiredFeatures: ['policy.cancel.create']
      },
      PROVIDER_SELECTOR: {
        rolesListWithReadAccess: [ROLE.ADMIN],
        rolesListWithWriteAccess: [ROLE.ADMIN],
        requiredFeatures: ['policy.cancel.list']
      }
    }
  },
  {
    route: '/policies/cancel-policies/create',
    exact: true,
    rolesListWithReadAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ADMIN,
      ROLE.CHANNEL_ACTIVATION
    ],
    rolesListWithWriteAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ACTIVATION
    ],
    requiredFeatures: ['policy.cancel.create'],
    component: <PoliciesForm />
  },
  {
    route: '/policies/cancel-policies/:providerCode/:id/edit',
    exact: true,
    rolesListWithReadAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ADMIN,
      ROLE.CHANNEL_ACTIVATION
    ],
    rolesListWithWriteAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ACTIVATION
    ],
    requiredFeatures: ['policy.cancel.edit'],
    component: <PoliciesForm />
  },
  {
    route: '/policies/amendment-policies/create',
    exact: true,
    rolesListWithReadAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ADMIN,
      ROLE.CHANNEL_ACTIVATION
    ],
    rolesListWithWriteAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ACTIVATION
    ],
    requiredFeatures: ['policy.amendment.create'],
    component: <ModifyPolicyForm />
  },
  {
    route: '/policies/amendment-policies/:providerCode/:id/edit',
    exact: true,
    rolesListWithReadAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ADMIN,
      ROLE.CHANNEL_ACTIVATION
    ],
    rolesListWithWriteAccess: [
      ROLE.ADMIN,
      ROLE.PROVIDER,
      ROLE.PROVIDER_MARKETING,
      ROLE.PROVIDER_IT,
      ROLE.PROVIDER_MANAGEMENT,
      ROLE.CHANNEL_ACTIVATION
    ],
    requiredFeatures: ['policy.amendment.edit'],
    component: <ModifyPolicyForm />
  },
  // Add-on policies get their own route rather than a third branch of the one above: that
  // route requires the cancel AND amendment list features, and add-on access is granted
  // separately (policy.addon.*). PoliciesTabs renders all three tabs from either route.
  {
    route: ADD_ON_POLICIES_ROUTE,
    exact: true,
    rolesListWithReadAccess: POLICY_READERS,
    rolesListWithWriteAccess: POLICY_WRITERS,
    requiredFeatures: ['policy.addon.list'],
    component: <PoliciesTabs />,
    tabs: [
      {
        params: { page: 'add-on-policies' },
        rolesListWithReadAccess: POLICY_READERS,
        rolesListWithWriteAccess: POLICY_WRITERS,
        requiredFeatures: ['policy.addon.list']
      }
    ],
    sections: {
      ADD_POLICY_BUTTON: {
        rolesListWithReadAccess: POLICY_READERS,
        rolesListWithWriteAccess: POLICY_WRITERS,
        requiredFeatures: ['policy.addon.create']
      },
      EDIT_POLICY: {
        rolesListWithReadAccess: POLICY_READERS,
        rolesListWithWriteAccess: POLICY_WRITERS,
        requiredFeatures: ['policy.addon.edit']
      },
      DELETE_POLICY: {
        rolesListWithReadAccess: POLICY_READERS,
        rolesListWithWriteAccess: POLICY_WRITERS,
        requiredFeatures: ['policy.addon.delete']
      },
      PROVIDER_SELECTOR: {
        rolesListWithReadAccess: [ROLE.ADMIN],
        rolesListWithWriteAccess: [ROLE.ADMIN],
        requiredFeatures: ['policy.addon.list']
      }
    }
  },
  {
    route: '/policies/add-on-policies/create',
    exact: true,
    rolesListWithReadAccess: POLICY_READERS,
    rolesListWithWriteAccess: POLICY_WRITERS,
    requiredFeatures: ['policy.addon.create'],
    component: <AddOnPoliciesForm />
  },
  {
    route: '/policies/add-on-policies/:providerCode/:id/edit',
    exact: true,
    rolesListWithReadAccess: POLICY_READERS,
    rolesListWithWriteAccess: POLICY_WRITERS,
    requiredFeatures: ['policy.addon.edit'],
    component: <AddOnPoliciesForm />
  }
]

export default cancelPolicyPagesRoleManagement
